/**
 * addressDiff — line-by-line comparison of an entered address with the USPS
 * suggestion, for the "USPS suggests a correction" bar (Collette Cruse
 * life-ring report 2026-10-16: "it changed it without approval"; Bill approved
 * 2026-10-16). The officer sees exactly what would change before clicking
 * "Accept Standardized"; nothing is applied without that click.
 *
 * Pure — no React — so the comparison is unit-tested.
 */

export interface AddressLines {
  street: string;
  city: string;
  state: string;
  zip: string;
}

export interface AddressDiffPart {
  text: string;
  /** True when this word is not in the current value (shown highlighted). */
  changed: boolean;
}

export interface AddressDiffRow {
  label: 'Street' | 'City' | 'State' | 'ZIP';
  current: string;
  suggested: string;
  /** True when the two values differ (ignoring case, punctuation and spacing). */
  changed: boolean;
  /** The suggested value split into words, each flagged when new. */
  suggestedParts: ReadonlyArray<AddressDiffPart>;
}

function norm(v: string): string {
  return v.toUpperCase().replace(/[.,#]/g, ' ').replace(/\s+/g, ' ').trim();
}

function words(v: string): string[] {
  return v.split(/\s+/).filter((w) => w.length > 0);
}

/** Split `suggested` into words, flagging each word the current value does not contain. */
export function diffWords(current: string, suggested: string): AddressDiffPart[] {
  const have = new Set(words(norm(current)));
  return words(suggested).map((w) => ({ text: w, changed: !have.has(norm(w)) }));
}

/**
 * One row per address line. A row is `changed` only when the values really
 * differ; a blank suggestion (USPS returned nothing for that line) is never
 * shown as a change.
 */
export function addressDiffRows(current: AddressLines, suggested: AddressLines): AddressDiffRow[] {
  const rows: Array<[AddressDiffRow['label'], string, string]> = [
    ['Street', current.street, suggested.street],
    ['City', current.city, suggested.city],
    ['State', current.state, suggested.state],
    ['ZIP', current.zip, suggested.zip],
  ];
  return rows.map(([label, cur, sug]) => {
    const c = (cur ?? '').trim();
    const s = (sug ?? '').trim();
    const changed = s !== '' && norm(c) !== norm(s);
    return {
      label,
      current: c,
      suggested: s === '' ? c : s,
      changed,
      suggestedParts: changed ? diffWords(c, s) : words(s === '' ? c : s).map((t) => ({ text: t, changed: false })),
    };
  });
}

/** True when any line differs. */
export function addressHasChanges(rows: ReadonlyArray<AddressDiffRow>): boolean {
  return rows.some((r) => r.changed);
}
